/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { ChevronDown } from '@carbon/react/icons';
import Copier from './Copier';
import { isExpandable } from '../utils/common';

function ExpandableDisplay({ copyableContent, expanded, onExpand, expandLinesAfter = 8, children }) {
  const [expandedState, updateExpandedState] = useState(expanded || false);
  const [expandable, updateExpandable] = useState(false);

  const { t } = useTranslation();
  const copyRef = useRef(`copyable_content_${uniqueId()}`);
  const copierRef = useRef(null);

  useEffect(() => {
    updateExpandedState(expanded);
  }, [expanded]);

  useEffect(() => {
    const elementNode = children ? document.querySelector(`.${copyRef.current}`) : null;
    updateExpandable(isExpandable(elementNode ? elementNode.textContent : null, expandLinesAfter));
  }, [expandLinesAfter, copyableContent, children]);

  if (!onExpand) {
    onExpand = () => {
      updateExpandedState(!expandedState);
    };
  }

  const moreLessButton = (
    <button
      className={classNames(`cds--btn`, `cds--btn--ghost`, `cds--btn--field`, `cds--snippet-btn--expand`)}
      type="button"
      onClick={onExpand}
      data-testid="more-less-button"
    >
      <span className="cds--snippet-btn--text">{expandedState ? t('show_less') : t('show_more')}</span>
      <ChevronDown
        aria-label={expandedState ? t('show_less') : t('show_more')}
        className="cds--icon-chevron--down cds--snippet__icon"
        name="chevron--down"
      />
    </button>
  );

  return (
    <div className="expandableDisplay" data-testid="expandableDisplay">
      <div
        className={classNames('cds--snippet', 'cds--snippet--multi', 'cds--snippet--wraptext', { 'cds--snippet--expand': expandedState })}
        data-testid="content_panel"
      >
        <div
          className={classNames('cds--snippet-container', 'body', { expanded: expandedState }, { small: !expandable })}
          aria-label="Scrollable code block"
          data-testid="expandable-display"
        >
          <div ref={copierRef} className={`boundedSection ${  !copyableContent && copyRef.current}`} data-testid="default-copyable-content">
            {children}
          </div>
        </div>
        {copyableContent && (
          <div className={classNames('hiddenCopy', copyRef.current)} data-testid="copyable-content">
            <pre>
              <code>{copyableContent}</code>
            </pre>
          </div>
        )}
        <Copier element={`.${copyRef.current}`} copierRef={copierRef} />
        {expandable && moreLessButton}
      </div>
    </div>
  );
}

ExpandableDisplay.propTypes = {
  copyableContent: PropTypes.string, // If you want a specific copyable content
  expanded: PropTypes.bool, // If you want to control the expanded state
  onExpand: PropTypes.func, // If you want to control the expanded state
  expandLinesAfter: PropTypes.number, // If you want to set a custom numbers of lines to trigger the expand button (Defaults to 8)
  children: PropTypes.node // If you want additional elements displayed under the JSON Viewer
};

export default ExpandableDisplay;
